/* EU VAT number on the order page (Sandro, 2026-10-06). The EU Tax Exemption Easy app (theme
   app embed, settings in config/settings_data.json) checks the number with VIES in the slot
   under the total. Once it is valid, the app turns the buy button into its VAT-free checkout:
   it reads the cart and opens a draft order without VAT. The app only shows when the cart has
   items and only prices what the cart holds, so from the moment a company opens the VAT row the
   cart follows the configuration. This file also keeps the button in the shop's style and
   prints the price without VAT under the total. Only rendered in the markets of the section
   setting vat_app_markets (sections/lb-configure.liquid). */
(function () {
  'use strict';

  /* The app's own storage key for a checked VAT number (its tax-widget-manager.js). */
  var VALIDATION_KEY = 'tax_exempt_b2b_validation';
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var SYNC_DELAY = 250;
  var BUTTON_INNER = '<span data-lb-cfg-pay-label></span><span class="lb-arrow" aria-hidden="true">→</span>';

  /* 2026-10-16: the app prices shipping with a sample address per country, and its samples for
     Romania and Ireland have no county, which Shopify requires there. The lookup failed, the app
     showed "Shipping rates are temporarily unavailable" and retried it about three times a
     second. Fill in the county of the sample's city (Bucharest, Dublin) when it is missing. */
  var RATE_COUNTY = { Romania: 'B', Ireland: 'D' };
  var RATE_URL = /\/cart\/(prepare_|async_)?shipping_rates\.json\?/;

  function withCounty(url) {
    var parts = url.split('?');
    var params = new URLSearchParams(parts[1]);
    var county = RATE_COUNTY[params.get('shipping_address[country]')];
    if (!county || params.get('shipping_address[province]')) return url;
    params.set('shipping_address[province]', county);
    return parts[0] + '?' + params.toString();
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var request = typeof input === 'string' && RATE_URL.test(input) ? withCounty(input) : input;
      return nativeFetch.call(window, request, init);
    };
  }

  function hasValidation() {
    try { return !!window.localStorage.getItem(VALIDATION_KEY); } catch (error) { return false; }
  }

  /* €5,461.34: English grouping like the rest of the page, always with cents (what is charged). */
  function money(amount, symbol) {
    var parts = Math.abs(amount).toFixed(2).split('.');
    return (amount < 0 ? '-' : '') + symbol + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  function readSymbol() {
    var node = document.querySelector('[data-lb-cfg-data]');
    try { return JSON.parse(node.textContent).symbol || '€'; } catch (error) { return '€'; }
  }

  function initVatApp(root) {
    var box = root.querySelector('[data-lb-vat-app]');
    var payButton = root.querySelector('[data-lb-cfg-pay]');
    var form = root.querySelector('#lb-cfg-form');
    if (!box || !payButton || !form || root.getAttribute('data-mode') !== 'live') return;

    var net = root.querySelector('[data-lb-vat-net]');
    var netValue = root.querySelector('[data-lb-vat-net-value]');
    var dockTotal = root.querySelector('[data-lb-cfg-dock-total]');
    var status = root.querySelector('[data-lb-cfg-status]');
    var label = box.getAttribute('data-label') || 'Order without VAT';
    var symbol = readSymbol();
    var following = false;
    var synced = '';
    var queue = Promise.resolve(true);
    var timer = null;
    var releasing = false;
    var wasVatFree = false;

    function post(url, body) {
      return fetch(url, { method: 'POST', headers: JSON_HEADERS, body: body ? JSON.stringify(body) : undefined })
        .then(function (response) {
          if (!response.ok) throw new Error(url + ' ' + response.status);
          return response;
        });
    }

    /* The first time an empty cart is left alone: clearing makes the app read an empty cart,
       which it then trusts for a few seconds. */
    function emptyCart() {
      var empty = synced ? Promise.resolve(false) : fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (cart) { return !!cart && cart.item_count === 0; });
      return empty.then(function (isEmpty) { return isEmpty ? null : post('/cart/clear.js'); });
    }

    /* The app listens for this to show its form and reprice the button. It caches "the cart is
       empty" for 2.5 s, so ask once more after that if its form is still missing. */
    function nudgeApp() {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      setTimeout(function () {
        if (!box.querySelector('[data-tax-widget]')) document.dispatchEvent(new CustomEvent('cart:updated'));
      }, 2700);
    }

    /* Make the cart hold exactly the configuration, one update at a time. Resolves to whether
       the cart matches. */
    function sync() {
      clearTimeout(timer);
      timer = null;
      queue = queue.then(function () {
        var wanted = root.getAttribute('data-lb-cfg-items') || '[]';
        if (wanted === '[]') return false;
        if (wanted === synced) return true;
        return emptyCart()
          .then(function () { return post('/cart/add.js', { items: JSON.parse(wanted) }); })
          .then(function () {
            synced = wanted;
            nudgeApp();
            return true;
          });
      }).catch(function () {
        synced = '';
        return false;
      });
      return queue;
    }

    function follow() {
      if (following) return;
      following = true;
      sync();
    }

    function vatFreeReady() {
      var data = payButton.dataset;
      return data.b2bEnabled === 'true' && !!(data.b2bEmail && data.b2bVatNumber && data.b2bCountryCode);
    }

    function setLabel(text) {
      var current = payButton.querySelector('[data-lb-cfg-pay-label]');
      if (current && payButton.children.length === 2 && current.textContent === text) return;
      payButton.innerHTML = BUTTON_INNER;
      payButton.querySelector('[data-lb-cfg-pay-label]').textContent = text;
    }

    /* The app writes its own label and colours onto the button: keep the shop's button and show
       the price without VAT on it, under the total and in the phone dock. */
    function paint() {
      var price = parseFloat(payButton.getAttribute('data-b2b-price'));
      var vatFree = payButton.getAttribute('data-b2b-enabled') === 'true' && isFinite(price);
      if (payButton.hasAttribute('style')) payButton.removeAttribute('style');

      if (vatFree) {
        setLabel(payButton.dataset.processing === 'true' ? 'One moment' : label + ' · ' + money(price, symbol));
        if (netValue) netValue.textContent = money(price, symbol);
        if (dockTotal) dockTotal.textContent = money(price, symbol);
        if (net) net.hidden = false;
        root.classList.add('is-vat-free');
        wasVatFree = true;
        return;
      }

      if (net) net.hidden = true;
      root.classList.remove('is-vat-free');
      var missing = !payButton.querySelector('[data-lb-cfg-pay-label]');
      if (missing) payButton.innerHTML = BUTTON_INNER;
      if (missing || wasVatFree) {
        wasVatFree = false;
        root.dispatchEvent(new CustomEvent('lb-cfg:refresh'));
      }
    }

    box.addEventListener('toggle', function () {
      if (box.open) follow();
    });

    root.addEventListener('lb-cfg:change', function () {
      if (!following) return;
      clearTimeout(timer);
      timer = setTimeout(sync, SYNC_DELAY);
    });

    /* A VAT-free click waits until the cart holds what is on screen, then goes on to the app. */
    root.addEventListener('click', function (event) {
      if (releasing || !vatFreeReady() || !event.target.closest || event.target.closest('[data-lb-cfg-pay]') !== payButton) return;
      event.preventDefault();
      event.stopPropagation();
      sync().then(function (ready) {
        if (!ready) {
          if (status) {
            status.textContent = 'Something went wrong reaching checkout. Try again, or write to us and we will sort it out.';
            status.setAttribute('data-kind', 'error');
          }
          return;
        }
        releasing = true;
        payButton.click();
        releasing = false;
      });
    }, true);

    /* The phone dock and the header's Order now submit the form: send those through the click. */
    root.addEventListener('submit', function (event) {
      if (event.target !== form || !vatFreeReady()) return;
      event.preventDefault();
      event.stopPropagation();
      payButton.click();
    }, true);

    new MutationObserver(paint).observe(payButton, {
      attributes: true,
      attributeFilter: ['data-b2b-enabled', 'data-b2b-price', 'data-processing', 'style'],
      childList: true
    });

    /* Back with a number checked earlier: open the row and let the app restore it. */
    if (hasValidation()) {
      box.open = true;
      follow();
    }
  }

  function init() {
    document.querySelectorAll('[data-lb-cfg]').forEach(initVatApp);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
